import { EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import InfoIcon from '@mui/icons-material/Info'
import MessageIcon from '@mui/icons-material/Message'
import { Box, Chip, Grid, Tooltip, Typography } from '@mui/material'
import useFeatureFlags from '@src/hooks/config-hooks'
import { calculateDaysUntilExpiration } from '@src/services/Formatter'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'
import CancelCreditAppButton from './CancelCreditAppButton'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
  canEdit: boolean
  financingProgramId: EFinancingProgram
  hasUnread: boolean
}
const LoanInfoBox = ({ creditApp, merchant, canEdit, financingProgramId, hasUnread }: Props) => {
  const { t } = useTranslation()
  const { flags } = useFeatureFlags()

  const handleOpenChat = () => {
    window.open(`/chat-dashboard/${financingProgramId}/${creditApp.id}`, '_blank')
  }
  return (
    <Grid m={1}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {creditApp.originSystemId !== EOriginSystemId.MerchantDashboard && (
          <Chip
            icon={
              <Tooltip
                title={
                  creditApp.originSystemId === EOriginSystemId.Backoffice
                    ? t('viewCreditApplication.limitedAccessBackOffice')
                    : t('viewCreditApplication.limitedAccessSubscription')
                }
              >
                <InfoIcon />
              </Tooltip>
            }
            label={t('viewCreditApplication.limitedAccess')}
            color="info"
          />
        )}
        {flags.UseInstantMessage && (
          <Chip
            icon={<MessageIcon />}
            label={hasUnread ? t('common.unreadMessages') : t('common.messages')}
            color={hasUnread ? 'warning' : 'info'}
            onClick={handleOpenChat}
            sx={{ cursor: 'pointer' }}
          />
        )}
        {creditApp.isInvisalign && (
          <Chip label={t('editCreditApplication.invisalign.indicator')} color="info" />
        )}
      </Box>
      <Typography variant="h4" fontWeight="bold" color="primary" component="div">
        {t('common.application')} #{creditApp.referenceNumber}
      </Typography>
      <Grid container sx={{ mt: 2 }} spacing={2} size={12}>
        <Grid size={6}>
          <Typography variant="subtitle1" gutterBottom>
            {t('viewCreditApplication.merchant')}
          </Typography>
          <Typography variant="body1">{merchant.name}</Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle1" gutterBottom>
            {t('viewCreditApplication.createdBy')}
          </Typography>
          <Typography variant="body1">{creditApp.createdByUserFullName}</Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle1" gutterBottom>
            {t('viewCreditApplication.createdOn')}
          </Typography>
          <Typography variant="body1">{formatDate(creditApp.createdOn)}</Typography>
        </Grid>
        <Grid mb={3} size={6}>
          <Typography variant="subtitle1" gutterBottom>
            {t('viewCreditApplication.expiresOn')}
          </Typography>
          <Typography variant="body1">
            {(() => {
              const daysUntilExpiration = calculateDaysUntilExpiration(creditApp.expiresOn)
              const dayLabel = daysUntilExpiration <= 1 ? t('common.day') : t('common.days')
              return `${formatDate(creditApp.expiresOn)} (${daysUntilExpiration} ${dayLabel})`
            })()}
          </Typography>
        </Grid>
      </Grid>
      {canEdit && creditApp.originSystemId === EOriginSystemId.MerchantDashboard && (
        <Grid size={4}>
          <CancelCreditAppButton creditApp={creditApp} />
        </Grid>
      )}
    </Grid>
  )
}

export default LoanInfoBox
